import SystemDashboard from '@/models/SystemDashboard'
import SystemDashboardWidget from '@/models/SystemDashboardWidget'
import SystemDashboardAction from '@/models/SystemDashboardAction'
import SelectOption from '@/models/interface/SelectOption'
import moment from 'moment'
import DeliveryGoalCard from '../widgets/delivery/DeliveryGoalCard'
import DeliveryDailyChart from '../widgets/delivery/DeliveryDailyChart'
import MonthlyBookChart from '../widgets/delivery/MonthlyBookChart'
import MonthlyBookTotalsChart from '../widgets/delivery/MonthlyBookTotalsChart'
import DailyGameDeliveryChart from '../widgets/delivery/DailyGameDeliveryChart'
import DeliveryBreakdownTable from '../widgets/delivery/DeliveryBreakdownTable'
import DeliveryBucketMix from '../widgets/delivery/DeliveryBucketMix'

/**
 * KAN-195 — Delivery dashboard.
 *
 * A whole-book view of delivered impressions split into Disney MLB /
 * Disney Non-MLB / Non-Disney, prompted by recent Disney delivery issues.
 *
 *   • Top: month-to-date delivery-vs-goal card, then the daily delivery chart
 *     (this month stacked bars + previous month reference lines, through
 *     yesterday).
 *   • Middle: three month-to-date breakdowns, each a full-width table plus a
 *     Disney-only share pie and an absolute stacked bucket-mix bar:
 *       1. by advertiser industry
 *       2. by sales channel (Holding / Direct / 3rd Party)
 *       3. by Disney account status (Approved / Blocklist Exception /
 *          Blocklist / Not Approved)
 *   • Bottom: advertiser-level breakdown, table only.
 *
 * Backed by the `delivery-dashboards/overview` endpoint (see `deliveryContract.ts`).
 */
export default class DeliveryModule extends SystemDashboard {
  public title: string = 'Disney Delivery'

  // Unique key — must match the `delivery_dashboard` option in
  // DefaultPermissions.ts (that's how dashboard access is granted) and must NOT
  // collide with another dashboard's key, or Vue reuses tab/component instances
  // across the duplicate <b-tab :key> and widgets render stale/cross-wired data.
  public key: string = 'delivery_dashboard'

  /**
   * Dashboard-wide filters, broadcast to every widget via DashboardView:
   *   - month: reporting month (YYYY-MM); the chart's reference series is
   *     always the month before the selected one. Defaults to current month.
   *   - show_nondisney: show or hide Non-Disney delivery (delivery a
   *     Disney-targeted line item served on non-Disney inventory). Hidden by
   *     default; totals follow the toggle.
   */
  public filters: any = {
    month: moment().format('YYYY-MM'),
    show_nondisney: 'hide',
  }

  public widgets: SystemDashboardWidget[] = [
    DeliveryModule.dashboardFilters(),
    new DeliveryGoalCard(),
    new DeliveryDailyChart(),

    // Pre-cached, each its own request (independent of the heavy `overview` call).
    new MonthlyBookChart(),
    new MonthlyBookTotalsChart(),
    new DailyGameDeliveryChart(),

    // Each grouping: breakdown table and bucket-split bar side by side.
    // Legend labels on the stacked bar are clickable to toggle buckets.
    ...DeliveryModule.groupingWidgets('by_industry', 'Advertiser Industry', 'Industry'),
    ...DeliveryModule.groupingWidgets('by_sales_channel', 'Sales Channel', 'Channel'),
    ...DeliveryModule.groupingWidgets('by_disney_status', 'Disney Account Status', 'Account Status'),

    // Advertiser-level breakdown — table only (no pie / bucket-mix), as the
    // advertiser count is too high for those charts. Sorted by total delivery.
    new DeliveryBreakdownTable({
      title: 'Delivery by Advertiser',
      key: 'delivery-by-advertiser',
      dataKey: 'by_advertiser',
      firstLabel: 'Advertiser',
      size: { lg: 12, md: 12 },
      // Stars advertisers whose Disney account status is approved, with an
      // All / Starred / Non-Starred toggle at the top of the table.
      starToggle: true,
    }),
  ]

  /**
   * The search bar is unused on this dashboard — returning custom right-side
   * content replaces it (see DashboardView), so we render nothing there and
   * the filter row below becomes the top of the dashboard.
   */
  public get customRight() {
    return '<span></span>'
  }

  /**
   * Dashboard-level filter row, top right: the reporting-month calendar picker
   * and the Shifted-off-Disney eye toggle.
   */
  private static dashboardFilters(): SystemDashboardWidget {
    // Month calendar picker (no label) — selecting a month refetches the
    // overview for it (+ the previous month as reference).
    const month = new SystemDashboardAction()
    month.type = 'month'
    month.target = 'filters'
    month.key = 'month'

    const eye = new SystemDashboardAction()
    eye.type = 'toggle'
    eye.target = 'filters'
    eye.key = 'show_nondisney'
    eye.label = 'Shifted off Disney'
    // Single eye button: shows the action a click performs (option `detail` is
    // the icon) — eye to reveal Shifted off Disney, eye-slash to hide it again.
    eye.options = [
      new SelectOption('Hide Shifted off Disney', 'hide', null, null, 'eye-slash'),
      new SelectOption('Show Shifted off Disney', 'show', null, null, 'eye'),
    ]

    const widget = new SystemDashboardWidget()
    widget.type = 'filter'
    widget.key = 'delivery-dashboard-filters'
    // Both controls live top right: month picker, then the eye toggle.
    widget.rightCol = [month, eye]
    widget.size = { lg: 12, md: 12, sm: 12 }

    return widget
  }

  private static groupingWidgets(
    dataKey: 'by_industry' | 'by_sales_channel' | 'by_disney_status',
    titleSuffix: string,
    firstLabel: string,
  ): SystemDashboardWidget[] {
    const slug = dataKey.replace('by_', '').replace(/_/g, '-')
    // Table and bucket-split bar share the row; the category pies are hidden
    // for now (re-add a DeliveryDistributionPie here to restore them).
    return [
      new DeliveryBreakdownTable({
        title: `Delivery by ${titleSuffix}`,
        key: `delivery-by-${slug}`,
        dataKey,
        firstLabel,
        size: { lg: 6, md: 12 },
      }),
      new DeliveryBucketMix({
        title: `${firstLabel} · Bucket Split`,
        key: `delivery-mix-${slug}`,
        dataKey,
        mode: 'stacked',
        size: { lg: 6, md: 12 },
      }),
    ]
  }
}
